import { blue } from "@ant-design/colors"
import { Button, Input, Modal, Progress, Select, Steps, Upload, UploadFile } from "antd"
import { useEffect, useState } from 'react'
import VSpace from '../common/VSpace'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUpload } from "@fortawesome/pro-duotone-svg-icons"
import { useAttachmentFolders, useAttachmentTags, useAttachmentsInvalidator } from "./attachmentsQueries"
import { uploadAttachment } from "./attachmentsApi"

export type Props = {
  isOpen: boolean
  customerId: number
  propertyId?: number
  jobId?: number
  defaultFolder: string
  defaultFileName?: string
  containerId?: string
  onClose: () => void
}

const UploadSteps = {
  ChooseFiles: 0,
  Options: 1,
  Upload: 2
}

type FileStatus = {
  percent: number
  status: 'success' | 'exception' | 'active'
}

const AttachmentUpload = ({ isOpen, customerId, propertyId, jobId: jobIdProp, defaultFolder, defaultFileName, containerId, onClose }: Props) => {
  const [fileNames, setFileNames] = useState<Record<string, string>>({})
  const [fileProgress, setFileProgress] = useState<Record<string, FileStatus>>({})
  const [folder, setFolder] = useState<string>(defaultFolder ?? '')
  const [jobId, setJobId] = useState<number | undefined>(jobIdProp)
  const [tags, setTags] = useState<string[]>([])
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [currentStep, setCurrentStep] = useState(UploadSteps.ChooseFiles)
  const [uploadsComplete, setUploadsComplete] = useState(false)
  const invalidator = useAttachmentsInvalidator()

  useEffect(() => setFolder(defaultFolder), [defaultFolder])

  const [container, setContainer] = useState<HTMLElement | undefined>()
  useEffect(() => {
    if (containerId) {
      setContainer(document.getElementById(containerId) ?? undefined)
    } else {
      setContainer(undefined)
    }
  }, [containerId])

  const foldersQuery = useAttachmentFolders(customerId, propertyId)
  const folderOptions = (foldersQuery.data ?? [])
    .filter(x => x.id !== '__All')
    .map(f => ({ label: f.folder, value: f.id }))

    const tagsQuery = useAttachmentTags(isOpen)
    const tagOptions = (tagsQuery?.data ?? []).map(t => ({ label: t, value: t }))

  const handleFolderChange = (folder: string) => {
    setFolder(folder)
    setJobId(foldersQuery.data?.find(x => x.id === folder)?.jobId)
  }

  const handleClose = () => {
    setCurrentStep(UploadSteps.ChooseFiles)
    setFileList([])
    setFileNames({})
    setFileProgress({})
    setUploadsComplete(false)
    onClose()
  }

  const uploadFiles = () => {
    setCurrentStep(step => step + 1)
    fileList.forEach(file => {
      uploadAttachment({
        attachment: {
          name: fileNames[file.uid],
          customerId,
          propertyId,
          jobId,
          folder,
          tags: tags.join(', ')
        },
        file: file.originFileObj as File,
        uid: file.uid,
        filename: file.name!,
        onError,
        onProgress,
        onSuccess,
      })
    })
  }

  const onError = (uid: string) => {
    setFileProgress(x => ({
      ...x,
      [uid]: { percent: 100, status: 'exception' }
    }))
  }

  const onSuccess = (uid: string) => {
    setFileProgress(x => ({
      ...x,
      [uid]: { percent: 100, status: 'success' }
    }))

    // give background processing a chance to create the thumbnail before reloading attachments
    setTimeout(updateUploadsComplete, 1000)
  }

  const onProgress = (uid: string, percent: number) => {
    setFileProgress(x => ({
      ...x,
      [uid]: { percent, status: 'active' }
    }))
  }

  const selectFiles = (fileList: UploadFile[]) => {
    setFileList(fileList)
    setFileNames(Object.fromEntries(fileList.map((f, i) => {
      const suffix = fileList.length > 1 ? (i + 1).toString() : ''
      const fileName = fileNames[f.uid]
        ?? (defaultFileName && defaultFileName + suffix)
        ?? f.name.replace(/\..*?$/, '')
      return [f.uid, fileName]
    })))
  }

  const updateName = (uid: string, newValue: string) => {
    setFileNames({
      ...fileNames,
      [uid]: newValue
    })
  }

  const canUpload = Object.values(fileNames).every(x => !!x) && !!folder
  const updateUploadsComplete = () => {
    setUploadsComplete(Object.values(fileProgress).every(x => x.status !== 'active'))
    invalidator.invalidateFolder()
    invalidator.invalidateCustomer()
  }

  const cancelButton = (disabled: boolean) => <Button key="cancel" onClick={handleClose} disabled={disabled}>Cancel</Button>
  const backButton = <Button key="back" onClick={() => setCurrentStep(s => s - 1)}>Back</Button>
  const nextButton = <Button type="primary" key="next" onClick={() => setCurrentStep(s => s + 1)}>Next</Button>
  const uploadButton = <Button type="primary" key="upload" disabled={!canUpload} onClick={uploadFiles}>Upload Attachments</Button>
  const doneButton = <Button type="primary" key="done" loading={!uploadsComplete} onClick={handleClose}>Done</Button>

  const footerButtons = () => {
    switch (currentStep) {
      case UploadSteps.ChooseFiles: return [cancelButton(false), nextButton]
      case UploadSteps.Options: return [backButton, uploadButton]
      case UploadSteps.Upload: return [cancelButton(uploadsComplete), doneButton]
      default: return []
    }
  }

  const labelStyle: React.CSSProperties = {
    width: '100%',
    fontWeight: 'initial'
  }

  return <Modal
    open={isOpen}
    title="Upload Attachments"
    footer={footerButtons()}
    onCancel={handleClose}
    getContainer={container}
  >
    <VSpace size="middle">
      <Steps
        current={currentStep}
        items={[
          { title: 'Choose files' },
          { title: 'Options' },
          { title: 'Upload' },
        ]}
      />
      {currentStep === UploadSteps.ChooseFiles &&
        <Upload.Dragger
          beforeUpload={() => false}
          fileList={fileList}
          onChange={({ fileList }) => selectFiles(fileList)}
          multiple
        >
          <FontAwesomeIcon icon={faUpload} style={{ fontSize: 36, color: blue.primary }} />
          <p>Click or drag to add files</p>
        </Upload.Dragger>
      }
      {currentStep === UploadSteps.Options &&
        <>
          <label style={labelStyle}>
            File name{fileList.length === 1 ? '' : 's'}
            <VSpace>
              {fileList.map((file) => {
                return <Input
                  key={file.uid}
                  value={fileNames[file.uid]}
                  onChange={e => updateName(file.uid, e.target.value)}
                  placeholder={file.name}
                  maxLength={150}
                />
              })}
            </VSpace>
          </label>
          <label style={labelStyle}>
            Folder
            <Select value={folder} onChange={handleFolderChange} options={folderOptions} style={{ width: '100%' }} />
          </label>

          <label style={labelStyle}>
            Tags
            <Select mode="tags" value={tags} onChange={x => setTags(x)} options={tagOptions} style={{ width: '100%' }} />
          </label>
        </>
      }
      {currentStep === UploadSteps.Upload &&
        fileList.map(file => {
          const progress = fileProgress[file.uid] ?? { percent: 0, status: 'active' }
          return <label key={file.uid} style={{ width: '100%' }}>
            {fileNames[file.uid]}
            <Progress percent={progress.percent} status={progress.status} />
          </label>
        })
      }
    </VSpace>
  </Modal >
}

export default AttachmentUpload
